import PropTypes from 'prop-types';
import { useState } from 'react';
import { Icon } from '@iconify/react';
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from 'react-router';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Badge,
} from '@mui/material';
import { useModal } from 'src/hooks/useModal';
import CustomDialog from 'src/common/CustomDialog';
import Chat from 'src/common/Chat/Chat';
import { useTranslation } from 'react-i18next';
import ForumIcon from '@mui/icons-material/Forum';
import useChatUnreadMessages from 'src/common/Chat/hooks/useChatUnreadMessages';

// ----------------------------------------------------------------------

// const ListSubheaderStyle = styled((props) => (
//   <ListSubheader disableSticky disableGutters {...props} />
// ))(({ theme }) => ({
//   ...theme.typography.overline,
//   marginTop: theme.spacing(3),
//   marginBottom: theme.spacing(2),
//   paddingLeft: theme.spacing(5),
//   color: theme.palette.text.primary,
// }));

export const ListItemStyle = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    prop !== 'isSubmenu' && prop !== 'hasDarkBackground',
})(({ theme, isSubmenu, hasDarkBackground }) => ({
  ...theme.typography.h6,
  height: 48,
  textTransform: 'capitalize',
  paddingLeft: isSubmenu ? theme.spacing(1) : theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: hasDarkBackground
    ? theme.palette.primary.contrastText
    : theme.palette.text.secondary,
  // '&:before': {
  //   top: 0,
  //   right: 0,
  //   width: 3,
  //   bottom: 0,
  //   content: "''",
  //   display: 'none',
  //   position: 'absolute',
  //   borderTopLeftRadius: 4,
  //   borderBottomLeftRadius: 4,
  //   backgroundColor: theme.palette.primary.main,
  // },
}));

export const ListItemIconStyle = styled(ListItemIcon)({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  isShow: PropTypes.bool,
  item: PropTypes.object,
};

export function NavItem({
  item,
  isShow,
  isSubmenu,
  additionalAction,
  hasDarkBackground = false,
}) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { title, path, icon, info, state, children } = item;
  const isActiveRoot = path
    ? !!matchPath({ path, end: false }, pathname)
    : false;

  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen(!open);
  };

  const activeRootStyle = {
    color: hasDarkBackground ? 'secondary.main' : 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(
      hasDarkBackground
        ? theme.palette.secondary.main
        : theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    '&:before': { display: 'block' },
  };

  const activeSubStyle = {};

  if (children) {
    return (
      <>
        <ListItemStyle
          hasDarkBackground={hasDarkBackground}
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                // sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((item) => {
                const { title, path, state } = item;
                const isActiveSub = path
                  ? !!matchPath({ path, end: false }, pathname)
                  : false;

                return (
                  <ListItemStyle
                    hasDarkBackground={hasDarkBackground}
                    key={title}
                    component={RouterLink}
                    to={path}
                    state={state}
                  >
                    <ListItemIconStyle>
                      <Box component="span" />
                    </ListItemIconStyle>
                    <ListItemText disableTypography primary={title} />
                  </ListItemStyle>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  return (
    <ListItemStyle
      hasDarkBackground={hasDarkBackground}
      isSubmenu={isSubmenu}
      component={RouterLink}
      to={path}
      state={state}
      onClick={additionalAction}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
      {...item.props}
    >
      {icon && <ListItemIconStyle>{icon}</ListItemIconStyle>}
      {isShow && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  isShow: PropTypes.bool,
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, isShow = true, ...other }) {
  const [open, , , toggleModal] = useModal();
  const { t } = useTranslation();
  const { hasUnreadMessages, clearUnreadMessages } =
    useChatUnreadMessages(open);

  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        const navItems = [
          ...items,
          {
            title: t('sidebar.chat_tab_title'),
            additionalAction: () => {
              toggleModal();
              clearUnreadMessages();
            },
            icon: (
              <Badge
                color="success"
                variant="dot"
                invisible={!hasUnreadMessages}
              >
                <ForumIcon />
              </Badge>
            ),
          },
        ];

        return (
          <List key={subheader} disablePadding>
            {/* {isShow && <ListSubheaderStyle>{'enriquito <3'}</ListSubheaderStyle>} */}
            <Divider />
            {navItems.map((item) => (
              <NavItem
                key={item.title}
                item={item}
                isShow={isShow}
                hasDarkBackground
                {...(item.additionalAction && {
                  additionalAction: item.additionalAction,
                })}
              />
            ))}
          </List>
        );
      })}
      <CustomDialog open={open} onClose={toggleModal} maxWidth="lg">
        <Chat />
      </CustomDialog>
    </Box>
  );
}
